/**
 * @file useWarehousesPage.ts
 * @description Данные и операции справочника складов (phase19.23, A6).
 *
 * Вынесено из страницы (SRP): странице остаётся раскладка и inline-форма,
 * хуку — список, черновик и четыре операции (создать, сохранить, сделать
 * основным, деактивировать). Сервер отвечает 409
 * `warehouse_has_active_reservations` — текст показываем как есть.
 */

import { useCallback, useEffect, useState } from "react";

import { useWmsConfirm } from "./use-wms-confirm";
import type { WarehouseDraft } from "./WarehouseDraftForm";
import { useNotify } from "../ui/NotifyProvider";
import { warehousesApi, type WarehouseDto } from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";

/** Пустой черновик — он же дефолт формы создания. */
export const EMPTY_WAREHOUSE_DRAFT: WarehouseDraft = {
  name: "",
  code: "",
  address: "",
  phone: "",
  workingHours: "",
  kind: "FINISHED_GOODS",
  isSellable: true,
};

export function useWarehousesPage() {
  const notify = useNotify();
  const asker = useWmsConfirm();
  const [items, setItems] = useState<WarehouseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WarehouseDraft>(EMPTY_WAREHOUSE_DRAFT);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await warehousesApi.list());
      setError(null);
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось загрузить склады"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function startEdit(w: WarehouseDto) {
    setEditingId(w.id);
    setDraft({
      name: w.name,
      code: w.code,
      address: w.address ?? "",
      phone: w.phone ?? "",
      workingHours: w.workingHours ?? "",
      kind: w.kind ?? "FINISHED_GOODS",
      isSellable: w.isSellable ?? true,
      isDefault: w.isDefault,
    });
    setCreating(false);
  }

  function startCreate() {
    setCreating(true);
    setEditingId(null);
    setDraft(EMPTY_WAREHOUSE_DRAFT);
  }

  function cancel() {
    setEditingId(null);
    setCreating(false);
    setDraft(EMPTY_WAREHOUSE_DRAFT);
  }

  /** Создание и правка — один запрос с общим телом, отличается только метод. */
  async function save() {
    if (!draft.name.trim() || !draft.code.trim()) return;
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      code: draft.code.trim(),
      address: draft.address.trim() || null,
      phone: draft.phone.trim() || null,
      workingHours: draft.workingHours.trim() || null,
      kind: draft.kind,
      isSellable: draft.isSellable,
      isDefault: draft.isDefault,
    };
    try {
      if (editingId) {
        await warehousesApi.update(editingId, payload);
        notify.success("Склад обновлён");
      } else {
        await warehousesApi.create({
          ...payload,
          address: payload.address ?? undefined,
          phone: payload.phone ?? undefined,
          workingHours: payload.workingHours ?? undefined,
        });
        notify.success("Склад создан");
      }
      cancel();
      await refresh();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось выполнить операцию со складом"));
    } finally {
      setSaving(false);
    }
  }

  async function setDefault(w: WarehouseDto) {
    try {
      await warehousesApi.update(w.id, { isDefault: true });
      await refresh();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось выполнить операцию со складом"));
    }
  }

  async function deactivate(w: WarehouseDto) {
    // Окно кабинета вместо системного `confirm()` (phase19.31).
    const ok = await asker.confirm({
      title: `Деактивировать склад «${w.name}»?`,
      description: "Если на складе есть остатки, сервер откажет.",
      action: "Деактивировать",
      danger: true,
    });
    if (!ok) {
      return;
    }
    try {
      await warehousesApi.deactivate(w.id);
      notify.success("Склад деактивирован");
      await refresh();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось выполнить операцию со складом"));
    }
  }

  return {
    items,
    loading,
    error,
    refresh,
    creating,
    editingId,
    draft,
    setDraft,
    saving,
    startEdit,
    startCreate,
    cancel,
    save,
    setDefault,
    deactivate,
    /** Вопрос «Деактивировать склад?» — рисует страница (`WmsConfirmDialog`). */
    confirmRequest: asker.request,
    settleConfirm: asker.settle,
  };
}
